import isIos from 'is-ios'
import { BarMitzvahType } from 'types/types'
import { computed, Ref, shallowReactive } from 'vue'

import { useAnnotationCache } from '@/composables/useAnnotationCache'
import { COLORATION_INTERACTION_GUARD_MS, DEFAULT_KAMATZ_KATAN_COLOR, DEFAULT_SHEWA_NA_COLOR } from '@/constants/annotationConstants'
import ClearSentenceColors from '@/mutations/ClearSentenceColors.graphql'
import ClearSentenceWhitespace from '@/mutations/ClearSentenceWhitespace.graphql'
import SetAnnotationsOnCharacters from '@/mutations/SetAnnotationsOnCharacters.graphql'
import store from '@/store'
import { ReportingMutation, runReportedMutation } from '@/store/mutationHelpers'
import { AnnotatedCharacter } from '@/util/AnnotatedCharacter'
import { AnnotatedSegment } from '@/util/AnnotatedSegment'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import { AnnotatedWord } from '@/util/AnnotatedWord'
import { AnnotationStyle, serializeAnnotationStyle } from '@/util/annotationStyle'
import { isCharacter, splitIntoWords } from '@/util/CharacterClasses'
import { getKamatzKatanConsonants, getShewaNaConsonants } from '@/util/VowelDetection'

/** What the newline brush toggles: a line break ('break' → newlineAfter) or a setuma mid-line gap ('gap' → stretchAfter). */
export type NewlineBrushKind = 'break' | 'gap'

interface CharSelectionEntry {
  annotatedChar: AnnotatedCharacter
  charIndex: number
  segmentIndex: number
  sentenceIndex: number
  wordIndex: number
}

interface UseTextColorationOptions {
  barMitzvah: Ref<BarMitzvahType | null>
  disabled: Ref<boolean>
  overlayId?: Ref<string | null>
  sentenceCharOffsets?: Ref<Map<string, number>>
  sentenceIds: Ref<string[] | null>
}

function allCharsInSentence(annotatedSentence: AnnotatedSentence, sentenceIndex: number): CharSelectionEntry[] {
  const result: CharSelectionEntry[] = []
  annotatedSentence.forEach((segment, _boundary, segmentIndex = 0) => {
    segment.forEach((word, _wb, wordIndex = 0) => {
      word.forEach((char, charIndex = 0) => {
        if (char) {
          result.push({ annotatedChar: char, charIndex: charIndex!, segmentIndex: segmentIndex!, sentenceIndex, wordIndex: wordIndex! })
        }
      })
    })
  })
  return result
}

function charsInWord(word: AnnotatedWord, sentenceIndex: number, segmentIndex: number, wordIndex: number): CharSelectionEntry[] {
  const result: CharSelectionEntry[] = []
  word.forEach((char, charIndex = 0) => {
    if (char) {
      result.push({ annotatedChar: char, charIndex: charIndex!, segmentIndex, sentenceIndex, wordIndex })
    }
  })
  return result
}

export function useTextColoration(options: UseTextColorationOptions) {
  const { invalidate: invalidateAnnotations } = useAnnotationCache()
  const { barMitzvah, disabled, sentenceIds } = options

  // After a dblclick selects a word, the next mousedown (from the user's immediate follow-up
  // click or a platform quirk) would reset the selection to a single char. Guard against this
  // by ignoring mousedown events that fire within a short window after a dblclick.
  let lastCharInteractionTime = 0

  const state = shallowReactive({
    active: false,
    backgroundColor: '',
    brushKind: 'break' as NewlineBrushKind,
    dragAnchor: null as CharSelectionEntry | null,
    dragStartSentenceIndex: null as number | null,
    fontSize: '',
    foregroundColor: '',
    isDragging: false,
    newlineBrushActive: false,
    selectedChars: [] as CharSelectionEntry[],
    underline: false,
  })

  const isAvailable = computed(() => !isIos && !!barMitzvah.value)

  function toggleColoration() {
    console.log('useTextColoration.toggleColoration', { active: state.active, barMitzvah: !!barMitzvah.value, disabled: disabled.value })
    if (disabled.value || !barMitzvah.value) {
      return
    }
    state.active = !state.active
    if (!state.active) {
      closeSelection()
      state.newlineBrushActive = false
    }
    console.log('useTextColoration.toggleColoration → active =', state.active)
  }

  function closeSelection() {
    console.log('useTextColoration.closeSelection → clearing', state.selectedChars.length, 'chars')
    state.selectedChars = []
    state.foregroundColor = ''
    state.backgroundColor = ''
    state.fontSize = ''
    state.underline = false
    state.isDragging = false
    state.dragAnchor = null
    state.dragStartSentenceIndex = null
  }

  // Sync toolbar state fields from an annotation style (used when selection changes).
  function syncStateFromStyle(style: AnnotationStyle) {
    state.foregroundColor = style.color ?? ''
    state.backgroundColor = style.backgroundColor ?? ''
    state.fontSize = style.fontSize ?? ''
    state.underline = !!style.underline
  }

  // Map a character's stripped-text index back to the normalized (full-text) index
  // the backend expects. Accounts for vowel/cantillation stripping and sentence
  // trimming offsets (column mode).
  function normalizedCharIndex(char: AnnotatedCharacter): number {
    const annotatedSentence = char.word().segment().sentence()
    const sentenceId = annotatedSentence.sentence().id
    const charOffset = options.sentenceCharOffsets?.value?.get(sentenceId) ?? 0
    return annotatedSentence.normalizedIndex(char.index()) + charOffset
  }

  function toggleNewlineBrush() {
    if (disabled.value || !barMitzvah.value) {
      return
    }
    state.newlineBrushActive = !state.newlineBrushActive
    if (state.newlineBrushActive) {
      closeSelection()
    } else {
      state.brushKind = 'break'
    }
  }

  function setBrushKind(kind: NewlineBrushKind) {
    state.brushKind = kind
  }

  // Toggle a line break ('break' → newlineAfter) or a setuma gap ('gap' →
  // stretchAfter) after the given word. Sets or clears the style key on the word's
  // last consonant and persists it. normalizedCharIndex maps stripped indices back
  // to normalized text.
  function handleBrushClick(_annotatedSentence: AnnotatedSentence, _annotatedSegment: AnnotatedSegment, annotatedWord: AnnotatedWord) {
    if (!state.active || !state.newlineBrushActive || disabled.value || !barMitzvah.value) {
      return
    }
    const styleKey: keyof AnnotationStyle = state.brushKind === 'gap' ? 'stretchAfter' : 'newlineAfter'

    // Scan ALL chars in the word for an existing annotation (not just lastChar —
    // after stripping, the annotated char may not be the last visible char).
    let existingChar: AnnotatedCharacter | null = null
    let lastConsonantChar: AnnotatedCharacter | null = null
    annotatedWord.forEach((c) => {
      if (c) {
        if (c.overrideStyle[styleKey]) {
          existingChar = c
        }
        if (isCharacter(c.character())) {
          lastConsonantChar = c
        }
      }
    })

    const sentenceId = annotatedWord.segment().sentence().sentence().id

    /** Save a single char's annotation and invalidate the cache. */
    function saveAndInvalidateChar(char: AnnotatedCharacter) {
      const annotation = { index: normalizedCharIndex(char), style: serializeAnnotationStyle(char.overrideStyle) }
      saveAnnotationsForSentence(sentenceId, [annotation]).then(() => {
        invalidateAnnotations(barMitzvah.value!.student.username)
      })
    }

    if (existingChar) {
      // REMOVE
      const charToUpdate = existingChar as AnnotatedCharacter
      delete charToUpdate.overrideStyle[styleKey]
      saveAndInvalidateChar(charToUpdate)
    } else if (lastConsonantChar) {
      // ADD on the last consonant
      const charToUpdate = lastConsonantChar as AnnotatedCharacter
      charToUpdate.overrideStyle[styleKey] = true
      saveAndInvalidateChar(charToUpdate)
    }

    lastCharInteractionTime = Date.now()
  }

  // Called from a click handler on the modal card. Clears the selection unless
  // a char mousedown/dblclick just fired (within the guard window).
  function handleClickOutside() {
    const elapsed = Date.now() - lastCharInteractionTime
    console.log('useTextColoration.handleClickOutside', {
      active: state.active,
      elapsed,
      guardMs: COLORATION_INTERACTION_GUARD_MS,
      selectedCount: state.selectedChars.length,
    })
    if (elapsed < COLORATION_INTERACTION_GUARD_MS) {
      console.log('useTextColoration.handleClickOutside → skipped (interaction guard)')
      return
    }
    if (state.active && state.selectedChars.length) {
      closeSelection()
    }
  }

  function handleCharMousedown(
    _annotatedSentence: AnnotatedSentence,
    _annotatedSegment: AnnotatedSegment,
    _annotatedWord: AnnotatedWord,
    annotatedChar: AnnotatedCharacter,
    sentenceIndex: number,
    segmentIndex: number,
    wordIndex: number,
    charIndex: number,
  ) {
    console.log('useTextColoration.handleCharMousedown', {
      active: state.active,
      char: annotatedChar.character(),
      charIndex,
      disabled: disabled.value,
      segmentIndex,
      sentenceIndex,
      wordIndex,
    })
    if (!state.active || disabled.value || state.newlineBrushActive) {
      return
    }
    if (Date.now() - lastCharInteractionTime < COLORATION_INTERACTION_GUARD_MS) {
      console.log('useTextColoration.handleCharMousedown → skipped (interaction guard)')
      return
    }
    lastCharInteractionTime = Date.now()
    const anchor = { annotatedChar, charIndex, segmentIndex, sentenceIndex, wordIndex }
    state.isDragging = true
    state.dragAnchor = anchor
    state.dragStartSentenceIndex = sentenceIndex
    state.selectedChars = [anchor]
    syncStateFromStyle(annotatedChar.overrideStyle)
    console.log('useTextColoration.handleCharMousedown → selectedChars.length =', state.selectedChars.length)
  }

  function handleCharMouseenter(
    _annotatedSentence: AnnotatedSentence,
    _annotatedSegment: AnnotatedSegment,
    _annotatedWord: AnnotatedWord,
    annotatedChar: AnnotatedCharacter,
    sentenceIndex: number,
    segmentIndex: number,
    wordIndex: number,
    charIndex: number,
  ) {
    if (!state.isDragging || sentenceIndex !== state.dragStartSentenceIndex || !state.dragAnchor) {
      return
    }
    const current = { annotatedChar, charIndex, segmentIndex, sentenceIndex, wordIndex }
    // Recompute selection as just [anchor, current] so dragging back contracts the range
    if (state.dragAnchor.annotatedChar === annotatedChar) {
      state.selectedChars = [state.dragAnchor]
    } else {
      state.selectedChars = [state.dragAnchor, current]
    }
    console.log('useTextColoration.handleCharMouseenter → selection endpoints:', state.selectedChars.length)
  }

  function handleMouseup() {
    console.log('useTextColoration.handleMouseup', { isDragging: state.isDragging, selectedCount: state.selectedChars.length })
    if (!state.isDragging) {
      return
    }
    state.isDragging = false

    if (!state.selectedChars.length || state.dragStartSentenceIndex === null) {
      console.log('useTextColoration.handleMouseup → no selected chars or no dragStartSentenceIndex')
      return
    }

    // Fill the range: derive allChars from the selected chars' own sentence object
    // (NOT from annotatedSentences.value, which is a different instance than AnnotatedText's internal objects)
    const sentenceObj = state.selectedChars[0].annotatedChar.word().segment().sentence()
    const allChars = allCharsInSentence(sentenceObj, state.dragStartSentenceIndex)
    const selectedSet = new WeakSet(state.selectedChars.map((e) => e.annotatedChar))
    const positions = allChars.map((e, i) => (selectedSet.has(e.annotatedChar) ? i : -1)).filter((i) => i >= 0)
    console.log('useTextColoration.handleMouseup → allChars =', allChars.length, 'positions =', positions)
    if (!positions.length) {
      console.log('useTextColoration.handleMouseup → no matching positions, early return')
      return
    }
    const minPos = Math.min(...positions)
    const maxPos = Math.max(...positions)
    state.selectedChars = allChars.slice(minPos, maxPos + 1)
    console.log('useTextColoration.handleMouseup → filled range, selectedChars.length =', state.selectedChars.length)

    // Prevent the card-level @click (handleClickOutside) from immediately clearing the drag selection
    lastCharInteractionTime = Date.now()

    // Initialize toolbar state from first selected char
    if (state.selectedChars.length) {
      syncStateFromStyle(state.selectedChars[0].annotatedChar.overrideStyle)
    }
  }

  function handleCharDblclick(
    _annotatedSentence: AnnotatedSentence,
    _annotatedSegment: AnnotatedSegment,
    annotatedWord: AnnotatedWord,
    _annotatedChar: AnnotatedCharacter,
    sentenceIndex: number,
    segmentIndex: number,
    wordIndex: number,
  ) {
    if (!state.active || disabled.value || state.newlineBrushActive) {
      console.log('useTextColoration.handleCharDblclick → skipped (active=%s, disabled=%s)', state.active, disabled.value)
      return
    }
    console.log('useTextColoration.handleCharDblclick', {
      segmentIndex,
      sentenceIndex,
      word: annotatedWord.word(),
      wordIndex,
    })
    state.isDragging = false
    state.dragStartSentenceIndex = sentenceIndex
    state.selectedChars = charsInWord(annotatedWord, sentenceIndex, segmentIndex, wordIndex)
    lastCharInteractionTime = Date.now()
    console.log('useTextColoration.handleCharDblclick → selectedChars.length =', state.selectedChars.length)

    if (state.selectedChars.length) {
      syncStateFromStyle(state.selectedChars[0].annotatedChar.overrideStyle)
    }
  }

  // The overlay ID that all annotation mutations (save, clear) should target.
  // In column mode this points at the overlay-specific annotation set;
  // outside column mode it's undefined (→ global set, overlay=None on the backend).
  function currentOverlay(): string | undefined {
    return options.overlayId?.value || undefined
  }

  // Shared mutation helper: saves annotation entries for one sentence.
  /** Save a sentence's annotations. Resolves true once saved; a failure is shown to the teacher. */
  async function saveAnnotationsForSentence(sentenceId: string, annotations: { index: number; style: string }[]): Promise<boolean> {
    if (!barMitzvah.value) {
      return false
    }
    const payload = await runReportedMutation(
      SetAnnotationsOnCharacters,
      { annotations, barMitzvah: barMitzvah.value.student.username, overlay: currentOverlay(), sentence: sentenceId },
      'setAnnotationsOnCharacters',
      store.dispatch.snackbar.showError,
    )
    return !!payload
  }

  function saveBatch() {
    if (!state.selectedChars.length || !barMitzvah.value) {
      return
    }

    // Group by sentence (in practice, always one sentence in the focus window)
    const bySentence = new Map<string, CharSelectionEntry[]>()
    for (const entry of state.selectedChars) {
      const sentenceId = entry.annotatedChar.word().segment().sentence().sentence().id
      if (!bySentence.has(sentenceId)) {
        bySentence.set(sentenceId, [])
      }
      bySentence.get(sentenceId)!.push(entry)
    }

    const promises: Promise<boolean>[] = []
    for (const [sentenceId, entries] of bySentence) {
      const annotations = entries.map((e) => ({
        index: normalizedCharIndex(e.annotatedChar),
        style: serializeAnnotationStyle(e.annotatedChar.overrideStyle),
      }))
      promises.push(saveAnnotationsForSentence(sentenceId, annotations))
    }

    Promise.all(promises).then((saved) => {
      invalidateAnnotations(barMitzvah.value!.student.username)
      if (saved.every(Boolean)) {
        store.dispatch.snackbar.add({ message: 'Colors saved', state: 'primary' })
      }
    })
  }

  type StringStyleKey = 'color' | 'backgroundColor' | 'fontSize'

  function applyColor(attribute: StringStyleKey, value: string | { hex: string }) {
    const strValue = typeof value === 'string' ? value : value.hex
    // Sync the corresponding toolbar state field
    const stateMap: Record<StringStyleKey, keyof typeof state> = {
      backgroundColor: 'backgroundColor',
      color: 'foregroundColor',
      fontSize: 'fontSize',
    }
    ;(state[stateMap[attribute]] as string) = strValue
    for (const entry of state.selectedChars) {
      if (strValue) {
        entry.annotatedChar.overrideStyle[attribute] = strValue
      } else {
        delete entry.annotatedChar.overrideStyle[attribute]
      }
    }
    saveBatch()
  }

  function applyUnderline(value: boolean) {
    state.underline = value
    for (const entry of state.selectedChars) {
      if (value) {
        entry.annotatedChar.overrideStyle.underline = true
      } else {
        delete entry.annotatedChar.overrideStyle.underline
      }
    }
    saveBatch()
  }

  function clearSelectedAnnotations() {
    for (const entry of state.selectedChars) {
      const keys = Object.keys(entry.annotatedChar.overrideStyle) as (keyof AnnotationStyle)[]
      for (const key of keys) {
        delete entry.annotatedChar.overrideStyle[key]
      }
    }
    state.foregroundColor = ''
    state.backgroundColor = ''
    state.fontSize = ''
    state.underline = false
    saveBatch()
  }

  function clearSentences(mutation: typeof ClearSentenceColors, mutationKey: ReportingMutation, message: string) {
    if (!sentenceIds.value?.length || !barMitzvah.value) {
      return
    }

    store.dispatch.dialog.show({
      isRejectable: true,
      message,
      onAccept: () => {
        const promises = sentenceIds.value!.map((id) =>
          runReportedMutation(
            mutation,
            { barMitzvah: barMitzvah.value!.student.username, overlay: currentOverlay(), sentence: id },
            mutationKey,
            store.dispatch.snackbar.showError,
          ),
        )
        Promise.all(promises).then(() => {
          invalidateAnnotations(barMitzvah.value!.student.username)
          closeSelection()
        })
      },
      title: 'Are you sure?',
    })
  }

  function clearAllColors() {
    clearSentences(
      ClearSentenceColors,
      'clearSentenceColors',
      'This removes all colors and formatting from the current sentence (newlines are preserved).',
    )
  }

  function clearAllWhitespace() {
    clearSentences(
      ClearSentenceWhitespace,
      'clearSentenceWhitespace',
      'This removes all newlines from the current sentence (colors are preserved).',
    )
  }

  // Shared selection range: indices set + min/max base character positions.
  // Used by isCharSelected, isWordBoundarySelected, and isSegmentBoundarySelected.
  // Scoped to a single sentence via sentenceId to avoid matching characters at
  // the same offsetInSentence in other sentences.
  const selectionRange = computed(() => {
    if (!state.selectedChars.length) {
      return null
    }
    const indices = new Set(state.selectedChars.map((e) => e.annotatedChar.index()))
    const baseCharIndices = state.selectedChars.filter((e) => isCharacter(e.annotatedChar.character())).map((e) => e.annotatedChar.index())
    if (!baseCharIndices.length) {
      return null
    }
    const sentenceId = state.selectedChars[0].annotatedChar.word().segment().sentence().sentence().id
    return {
      indices,
      maxBaseIdx: Math.max(...baseCharIndices),
      minBaseIdx: Math.min(...baseCharIndices),
      sentenceId,
    }
  })

  // Use index-based comparison instead of object identity (WeakSet). AnnotatedText's
  // myAnnotatedSentences computed creates new AnnotatedCharacter instances on recompute,
  // so the objects emitted via events are different from the ones rendered in the template.
  // Character index (offsetInSentence) is stable across recomputations.
  // Returns CSS class string (with position: start/end) instead of boolean so AnnotatedText
  // can draw one continuous box instead of per-character outlines.
  const isCharSelected = computed<((char: AnnotatedCharacter) => string) | null>(() => {
    const range = selectionRange.value
    if (!range) {
      return null
    }
    return (char: AnnotatedCharacter) => {
      if (char.word().segment().sentence().sentence().id !== range.sentenceId) {
        return ''
      }
      if (!range.indices.has(char.index())) {
        return ''
      }
      let cls = 'colorationSelected'
      if (char.index() === range.minBaseIdx) {
        cls += ' colorationSelectedStart'
      }
      if (char.index() === range.maxBaseIdx) {
        cls += ' colorationSelectedEnd'
      }
      return cls
    }
  })

  // Returns CSS class for word boundary spans that fall inside the selection box.
  // The boundary after a word should be styled if the selection continues past it.
  const isWordBoundarySelected = computed<((word: AnnotatedWord) => string) | null>(() => {
    const range = selectionRange.value
    if (!range) {
      return null
    }
    return (word: AnnotatedWord) => {
      if (word.segment().sentence().sentence().id !== range.sentenceId) {
        return ''
      }
      const lastCharIdx = word.offsetInSentence() + word.nChars() - 1
      return lastCharIdx >= range.minBaseIdx && lastCharIdx < range.maxBaseIdx ? 'colorationSelected' : ''
    }
  })

  // Returns CSS class for segment boundary spans that fall inside the selection box.
  // NOTE: segment.nChars() only counts word characters, not boundary spaces between words.
  // We must derive the last character index from the segment's last word instead.
  const isSegmentBoundarySelected = computed<((segment: AnnotatedSegment) => string) | null>(() => {
    const range = selectionRange.value
    if (!range) {
      return null
    }
    return (segment: AnnotatedSegment) => {
      if (segment.sentence().sentence().id !== range.sentenceId) {
        return ''
      }
      let lastCharIdx = -1
      segment.forEach((word) => {
        lastCharIdx = word.offsetInSentence() + word.nChars() - 1
      })
      return lastCharIdx >= range.minBaseIdx && lastCharIdx < range.maxBaseIdx ? 'colorationSelected' : ''
    }
  })

  interface AutoColorOptions {
    kamatzKatanColor?: string
    shewaNaColor?: string
  }

  function autoColorVowels(annotatedSentences: AnnotatedSentence[], options: AutoColorOptions = {}) {
    if (!barMitzvah.value || !annotatedSentences.length) {
      return
    }

    const kamatzColor = options.kamatzKatanColor || DEFAULT_KAMATZ_KATAN_COLOR
    const shewaNaColor = options.shewaNaColor || DEFAULT_SHEWA_NA_COLOR

    // Collect all annotations to save, grouped by sentence ID
    const bySentence = new Map<string, { index: number; style: string }[]>()

    for (const annotatedSentence of annotatedSentences) {
      const sentenceId = annotatedSentence.sentence().id

      // Use the full normalized text (with vowels) for detection — the displayed
      // text may have vowels stripped in consonants/Torah mode, but consonant
      // indices are stable across all display modes.
      const fullWords = splitIntoWords(annotatedSentence.normalizedText())
      let wordCounter = 0

      annotatedSentence.forEach((segment) => {
        segment.forEach((word) => {
          const fullWordText = fullWords[wordCounter] ?? word.word()
          wordCounter++

          const kamatzPositions = getKamatzKatanConsonants(fullWordText)
          const shewaNaPositions = getShewaNaConsonants(fullWordText)

          if (!kamatzPositions.length && !shewaNaPositions.length) {
            return
          }

          // Map consonant indices to AnnotatedCharacter objects
          let consonantIndex = 0
          word.forEach((char) => {
            if (!char || !isCharacter(char.character())) {
              return
            }
            // Skip characters that already have a foreground color applied
            if (!char.overrideStyle.color) {
              let color: string | undefined
              if (kamatzPositions.includes(consonantIndex)) {
                color = kamatzColor
              } else if (shewaNaPositions.includes(consonantIndex)) {
                color = shewaNaColor
              }
              if (color) {
                char.overrideStyle.color = color
                if (!bySentence.has(sentenceId)) {
                  bySentence.set(sentenceId, [])
                }
                bySentence.get(sentenceId)!.push({
                  index: normalizedCharIndex(char),
                  style: serializeAnnotationStyle(char.overrideStyle),
                })
              }
            }
            consonantIndex++
          })
        })
      })
    }

    const promises = [...bySentence.entries()].map(([sentenceId, annotations]) => saveAnnotationsForSentence(sentenceId, annotations))

    if (promises.length) {
      Promise.all(promises).then((saved) => {
        invalidateAnnotations(barMitzvah.value!.student.username)
        if (saved.every(Boolean)) {
          store.dispatch.snackbar.add({ message: 'Auto-colored vowels saved', state: 'primary' })
        }
      })
    }
  }

  return {
    // Read-only state — consumers should not mutate these
    active: computed(() => state.active),
    applyColor,
    applyUnderline,
    autoColorVowels,
    backgroundColor: computed(() => state.backgroundColor),
    brushKind: computed(() => state.brushKind),
    clearAllColors,
    clearAllWhitespace,
    clearSelectedAnnotations,
    closeSelection,
    fontSize: computed(() => state.fontSize),
    foregroundColor: computed(() => state.foregroundColor),
    handleBrushClick,
    handleCharDblclick,
    handleCharMousedown,
    handleCharMouseenter,
    handleClickOutside,
    handleMouseup,
    hasSelection: computed(() => state.selectedChars.length > 0),

    isAvailable,

    isCharSelected,

    isSegmentBoundarySelected,

    isWordBoundarySelected,

    newlineBrushActive: computed(() => state.newlineBrushActive),
    // Read-only view of selected chars — for test inspection.
    // Cast as readonly to prevent consumers from mutating the array.
    selectedChars: computed(() => state.selectedChars as readonly CharSelectionEntry[]),
    setBrushKind,
    toggleColoration,
    toggleNewlineBrush,
    underline: computed(() => state.underline),
  }
}

export type { CharSelectionEntry }
